import type * as Types from '@hotelplan/supergraph-api';

import type { DocumentNode } from 'graphql';
import { DescriptionGeoLinkFragmentDoc } from './fdr-geo-breadcrumbs.generated';
import { FdrPaginationFragmentDoc } from 'schemas/fragment/regular/fdrPagination.generated';
import { FdrGeoProductFragmentDoc } from 'schemas/fragment/geo/fdrGeoProduct.generated';
import { FdrImageFragmentDoc } from 'schemas/fragment/regular/fdrImage.generated';
import { FdrResizedFragmentDoc } from 'schemas/fragment/regular/fdrResized.generated';
import { FdrGeoProductB2bFragmentDoc } from 'schemas/fragment/geo/fdrGeoProductB2b.generated';
import { FdrAllBlogArticlesResponseFragmentDoc } from 'schemas/fragment/blog-articles/fdr-all-blog-articles.generated';
import { FdrThemeGeoPagesFragmentDoc } from 'schemas/fragment/theme-geo/fdrThemeGeoPages.generated';
import { FdrRequestButtonFragmentDoc } from 'schemas/fragment/request-button/fdr-request-button.generated';
import { FdrBoosterFragmentDoc } from 'schemas/fragment/booster/fdr-booster.generated';
import type * as Apollo from '@apollo/client';
import * as ApolloReactHooks from '@hotelplan/fdr.lib.apollo.query';
const defaultOptions = {} as const;
export type FdrGeoModulesQueryVariables = Types.Exact<{
  input: Types.FdrGeoInput;
  childrenInput: Types.FdrGeoChildrenInput;
  productsInput: Types.FdrGeoProductsInput;
  hotelsInput: Types.FdrGeoProductsInput;
  blogCriteria: Array<Types.FdrImageCriteria> | Types.FdrImageCriteria;
  blogArticleInput: Types.FdrAllBlogArticlesInput;
  themeGeoInput: Types.FdrThemeGeoPagesInput;
  isB2B?: Types.InputMaybe<Types.Scalars['Boolean']>;
}>;

export type FdrGeoModulesQuery = {
  __typename?: 'Query';
  fdrGeo?:
    | {
        __typename: 'FdrContinent';
        id: string;
        tagLine?: { __typename?: 'FdrTagline'; text: string } | null;
        children: {
          __typename?: 'FdrContinentCountries';
          page?: Array<{
            __typename?: 'FdrCountry';
            name: string;
            id: string;
            hero?: {
              __typename?: 'FdrImage';
              alt: string;
              description?: string | null;
              copyright?: string | null;
              rawUrl: string;
              resized: Array<{
                __typename?: 'FdrResizedImage';
                id: string;
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              }>;
            } | null;
            webMeta: {
              __typename?: 'FdrGeoWebMeta';
              descriptionWebMeta?: {
                __typename?: 'FdrMeta';
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              } | null;
            };
          }> | null;
          pagination: {
            __typename?: 'FdrPagination';
            total: number;
            page: number;
            perPage: number;
          };
        };
        products?: {
          __typename?: 'FdrGeoProducts';
          productsPage?: Array<
            | {
                __typename: 'FdrAdventureTravel';
                teaser: string;
                name: string;
                id: string;
                bookmarked: boolean;
                headerImages?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                images?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                previews?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                route?: Array<{
                  __typename?: 'FdrAdventureTravelRoutePoint';
                  locationName: string;
                  coordinates: {
                    __typename?: 'FdrCoordinates';
                    latitude: number;
                    longitude: number;
                  };
                }> | null;
                webMeta: {
                  __typename?: 'FdrProductWebMeta';
                  meta?: {
                    __typename?: 'FdrMeta';
                    title: string;
                    description: string;
                    index: boolean;
                    noFollow: boolean;
                    sitemap: boolean;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  } | null;
                };
                featureGroups?: Array<{
                  __typename?: 'FdrProductFeatureGroup';
                  id: string;
                  name: string;
                  features: Array<{
                    __typename?: 'FdrProductFeature';
                    id: string;
                    name: string;
                    enabled: boolean;
                  }>;
                }> | null;
                productInfoB2B?: {
                  __typename?: 'ProductInfoB2B';
                  route?: string | null;
                  duration: string;
                  priceFrom?: string | null;
                  typeFlags?: Array<string> | null;
                } | null;
                googleStaticMapImage?: {
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                } | null;
              }
            | {
                __typename: 'FdrCruise';
                name: string;
                id: string;
                bookmarked: boolean;
              }
            | {
                __typename: 'FdrHotel';
                name: string;
                id: string;
                bookmarked: boolean;
                images?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                previews?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                webMeta: {
                  __typename?: 'FdrProductWebMeta';
                  meta?: {
                    __typename?: 'FdrMeta';
                    title: string;
                    description: string;
                    index: boolean;
                    noFollow: boolean;
                    sitemap: boolean;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  } | null;
                };
                flags?: {
                  __typename?: 'FdrHotelFlags';
                  extraOrdinary: boolean;
                } | null;
              }
            | {
                __typename: 'FdrRoundTrip';
                name: string;
                id: string;
                bookmarked: boolean;
              }
          > | null;
          pagination: {
            __typename?: 'FdrPagination';
            total: number;
            page: number;
            perPage: number;
          };
        };
        hotels: {
          __typename?: 'FdrGeoProducts';
          productsPage?: Array<
            | {
                __typename: 'FdrAdventureTravel';
                teaser: string;
                name: string;
                id: string;
                bookmarked: boolean;
                headerImages?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                images?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                previews?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                route?: Array<{
                  __typename?: 'FdrAdventureTravelRoutePoint';
                  locationName: string;
                  coordinates: {
                    __typename?: 'FdrCoordinates';
                    latitude: number;
                    longitude: number;
                  };
                }> | null;
                webMeta: {
                  __typename?: 'FdrProductWebMeta';
                  meta?: {
                    __typename?: 'FdrMeta';
                    title: string;
                    description: string;
                    index: boolean;
                    noFollow: boolean;
                    sitemap: boolean;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  } | null;
                };
                featureGroups?: Array<{
                  __typename?: 'FdrProductFeatureGroup';
                  id: string;
                  name: string;
                  features: Array<{
                    __typename?: 'FdrProductFeature';
                    id: string;
                    name: string;
                    enabled: boolean;
                  }>;
                }> | null;
                productInfoB2B?: {
                  __typename?: 'ProductInfoB2B';
                  route?: string | null;
                  duration: string;
                  priceFrom?: string | null;
                  typeFlags?: Array<string> | null;
                } | null;
              }
            | {
                __typename: 'FdrCruise';
                name: string;
                id: string;
                bookmarked: boolean;
              }
            | {
                __typename: 'FdrHotel';
                name: string;
                id: string;
                bookmarked: boolean;
                images?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                previews?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                webMeta: {
                  __typename?: 'FdrProductWebMeta';
                  meta?: {
                    __typename?: 'FdrMeta';
                    title: string;
                    description: string;
                    index: boolean;
                    noFollow: boolean;
                    sitemap: boolean;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  } | null;
                };
                flags?: {
                  __typename?: 'FdrHotelFlags';
                  extraOrdinary: boolean;
                } | null;
              }
            | {
                __typename: 'FdrRoundTrip';
                name: string;
                id: string;
                bookmarked: boolean;
              }
          > | null;
          pagination: {
            __typename?: 'FdrPagination';
            total: number;
            page: number;
            perPage: number;
          };
        };
        relatedBlogArticles: {
          __typename?: 'FdrAllBlogArticlesResponse';
          blogArticles?: Array<{
            __typename?: 'FdrBlogArticle';
            publicId: string;
            title?: string | null;
            text?: string | null;
            start?: any | null;
            flagline?: string | null;
            webMeta: {
              __typename?: 'FdrMeta';
              title: string;
              description: string;
              index: boolean;
              noFollow: boolean;
              sitemap: boolean;
              link: {
                __typename?: 'FdrLink';
                openMethod?: Types.FdrLinkOpenMethod | null;
                legacyUrl?: string | null;
                type: Types.FdrLinkType;
                url: string;
              };
            };
            hero?: {
              __typename?: 'FdrImage';
              resized: Array<{
                __typename?: 'FdrResizedImage';
                id: string;
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              }>;
            } | null;
            employee?: {
              __typename?: 'FdrEmployee';
              name: string;
              image?: {
                __typename?: 'FdrImage';
                resized: Array<{
                  __typename?: 'FdrResizedImage';
                  id: string;
                  link: {
                    __typename?: 'FdrLink';
                    openMethod?: Types.FdrLinkOpenMethod | null;
                    legacyUrl?: string | null;
                    type: Types.FdrLinkType;
                    url: string;
                  };
                }>;
              } | null;
            } | null;
          }> | null;
          pagination: {
            __typename?: 'FdrPagination';
            total: number;
            page: number;
            perPage: number;
          };
        };
        themeGeoPages: {
          __typename?: 'FdrThemeGeoPagesResponse';
          themeGeoPages?: Array<{
            __typename?: 'FdrThemeGeoPage';
            title: string;
            geo:
              | { __typename?: 'FdrContinent'; id: string }
              | { __typename?: 'FdrCountry'; id: string }
              | { __typename?: 'FdrCountryGroup'; id: string }
              | { __typename?: 'FdrDestination'; id: string }
              | { __typename?: 'FdrRegion'; id: string }
              | { __typename?: 'FdrResort'; id: string };
            theme: { __typename?: 'FdrTheme'; id: string };
            tagline?: { __typename?: 'FdrTagline'; text: string } | null;
            image?: {
              __typename?: 'FdrImage';
              alt: string;
              description?: string | null;
              copyright?: string | null;
              rawUrl: string;
              resized: Array<{
                __typename?: 'FdrResizedImage';
                id: string;
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              }>;
            } | null;
            webMeta: {
              __typename?: 'FdrMeta';
              link: {
                __typename?: 'FdrLink';
                openMethod?: Types.FdrLinkOpenMethod | null;
                legacyUrl?: string | null;
                type: Types.FdrLinkType;
                url: string;
              };
            };
          }> | null;
          pagination: {
            __typename?: 'FdrPagination';
            total: number;
            page: number;
            perPage: number;
          };
        };
        requestButton: {
          __typename?: 'FdrRequestButton';
          staticCaption: string;
          objectCaption?: string | null;
          link: {
            __typename?: 'FdrLink';
            openMethod?: Types.FdrLinkOpenMethod | null;
            legacyUrl?: string | null;
            type: Types.FdrLinkType;
            url: string;
          };
        };
        boosters?: Array<{
          __typename?: 'FdrBooster';
          id: string;
          mainText: string;
          frequency?: Types.BoosterFrequency | null;
          duration: number;
          delay?: number | null;
        }> | null;
      }
    | {
        __typename: 'FdrCountry';
        id: string;
        tagLine?: { __typename?: 'FdrTagline'; text: string } | null;
        children: {
          __typename?: 'FdrCountryRegions';
          page?: Array<{
            __typename?: 'FdrRegion';
            name: string;
            id: string;
            hero?: {
              __typename?: 'FdrImage';
              alt: string;
              description?: string | null;
              copyright?: string | null;
              rawUrl: string;
              resized: Array<{
                __typename?: 'FdrResizedImage';
                id: string;
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              }>;
            } | null;
            webMeta: {
              __typename?: 'FdrGeoWebMeta';
              descriptionWebMeta?: {
                __typename?: 'FdrMeta';
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              } | null;
            };
          }> | null;
          pagination: {
            __typename?: 'FdrPagination';
            total: number;
            page: number;
            perPage: number;
          };
        };
        products?: {
          __typename?: 'FdrGeoProducts';
          productsPage?: Array<
            | {
                __typename: 'FdrAdventureTravel';
                teaser: string;
                name: string;
                id: string;
                bookmarked: boolean;
                headerImages?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                images?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                previews?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                route?: Array<{
                  __typename?: 'FdrAdventureTravelRoutePoint';
                  locationName: string;
                  coordinates: {
                    __typename?: 'FdrCoordinates';
                    latitude: number;
                    longitude: number;
                  };
                }> | null;
                webMeta: {
                  __typename?: 'FdrProductWebMeta';
                  meta?: {
                    __typename?: 'FdrMeta';
                    title: string;
                    description: string;
                    index: boolean;
                    noFollow: boolean;
                    sitemap: boolean;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  } | null;
                };
                featureGroups?: Array<{
                  __typename?: 'FdrProductFeatureGroup';
                  id: string;
                  name: string;
                  features: Array<{
                    __typename?: 'FdrProductFeature';
                    id: string;
                    name: string;
                    enabled: boolean;
                  }>;
                }> | null;
                productInfoB2B?: {
                  __typename?: 'ProductInfoB2B';
                  route?: string | null;
                  duration: string;
                  priceFrom?: string | null;
                  typeFlags?: Array<string> | null;
                } | null;
                googleStaticMapImage?: {
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                } | null;
              }
            | {
                __typename: 'FdrCruise';
                name: string;
                id: string;
                bookmarked: boolean;
              }
            | {
                __typename: 'FdrHotel';
                name: string;
                id: string;
                bookmarked: boolean;
                images?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                previews?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                webMeta: {
                  __typename?: 'FdrProductWebMeta';
                  meta?: {
                    __typename?: 'FdrMeta';
                    title: string;
                    description: string;
                    index: boolean;
                    noFollow: boolean;
                    sitemap: boolean;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  } | null;
                };
                flags?: {
                  __typename?: 'FdrHotelFlags';
                  extraOrdinary: boolean;
                } | null;
              }
            | {
                __typename: 'FdrRoundTrip';
                name: string;
                id: string;
                bookmarked: boolean;
              }
          > | null;
          pagination: {
            __typename?: 'FdrPagination';
            total: number;
            page: number;
            perPage: number;
          };
        };
        hotels: {
          __typename?: 'FdrGeoProducts';
          productsPage?: Array<
            | {
                __typename: 'FdrAdventureTravel';
                teaser: string;
                name: string;
                id: string;
                bookmarked: boolean;
                headerImages?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                images?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                previews?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                route?: Array<{
                  __typename?: 'FdrAdventureTravelRoutePoint';
                  locationName: string;
                  coordinates: {
                    __typename?: 'FdrCoordinates';
                    latitude: number;
                    longitude: number;
                  };
                }> | null;
                webMeta: {
                  __typename?: 'FdrProductWebMeta';
                  meta?: {
                    __typename?: 'FdrMeta';
                    title: string;
                    description: string;
                    index: boolean;
                    noFollow: boolean;
                    sitemap: boolean;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  } | null;
                };
                featureGroups?: Array<{
                  __typename?: 'FdrProductFeatureGroup';
                  id: string;
                  name: string;
                  features: Array<{
                    __typename?: 'FdrProductFeature';
                    id: string;
                    name: string;
                    enabled: boolean;
                  }>;
                }> | null;
                productInfoB2B?: {
                  __typename?: 'ProductInfoB2B';
                  route?: string | null;
                  duration: string;
                  priceFrom?: string | null;
                  typeFlags?: Array<string> | null;
                } | null;
              }
            | {
                __typename: 'FdrCruise';
                name: string;
                id: string;
                bookmarked: boolean;
              }
            | {
                __typename: 'FdrHotel';
                name: string;
                id: string;
                bookmarked: boolean;
                images?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                previews?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                webMeta: {
                  __typename?: 'FdrProductWebMeta';
                  meta?: {
                    __typename?: 'FdrMeta';
                    title: string;
                    description: string;
                    index: boolean;
                    noFollow: boolean;
                    sitemap: boolean;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  } | null;
                };
                flags?: {
                  __typename?: 'FdrHotelFlags';
                  extraOrdinary: boolean;
                } | null;
              }
            | {
                __typename: 'FdrRoundTrip';
                name: string;
                id: string;
                bookmarked: boolean;
              }
          > | null;
          pagination: {
            __typename?: 'FdrPagination';
            total: number;
            page: number;
            perPage: number;
          };
        };
        relatedBlogArticles: {
          __typename?: 'FdrAllBlogArticlesResponse';
          blogArticles?: Array<{
            __typename?: 'FdrBlogArticle';
            publicId: string;
            title?: string | null;
            text?: string | null;
            start?: any | null;
            flagline?: string | null;
            webMeta: {
              __typename?: 'FdrMeta';
              title: string;
              description: string;
              index: boolean;
              noFollow: boolean;
              sitemap: boolean;
              link: {
                __typename?: 'FdrLink';
                openMethod?: Types.FdrLinkOpenMethod | null;
                legacyUrl?: string | null;
                type: Types.FdrLinkType;
                url: string;
              };
            };
            hero?: {
              __typename?: 'FdrImage';
              resized: Array<{
                __typename?: 'FdrResizedImage';
                id: string;
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              }>;
            } | null;
            employee?: {
              __typename?: 'FdrEmployee';
              name: string;
              image?: {
                __typename?: 'FdrImage';
                resized: Array<{
                  __typename?: 'FdrResizedImage';
                  id: string;
                  link: {
                    __typename?: 'FdrLink';
                    openMethod?: Types.FdrLinkOpenMethod | null;
                    legacyUrl?: string | null;
                    type: Types.FdrLinkType;
                    url: string;
                  };
                }>;
              } | null;
            } | null;
          }> | null;
          pagination: {
            __typename?: 'FdrPagination';
            total: number;
            page: number;
            perPage: number;
          };
        };
        themeGeoPages: {
          __typename?: 'FdrThemeGeoPagesResponse';
          themeGeoPages?: Array<{
            __typename?: 'FdrThemeGeoPage';
            title: string;
            geo:
              | { __typename?: 'FdrContinent'; id: string }
              | { __typename?: 'FdrCountry'; id: string }
              | { __typename?: 'FdrCountryGroup'; id: string }
              | { __typename?: 'FdrDestination'; id: string }
              | { __typename?: 'FdrRegion'; id: string }
              | { __typename?: 'FdrResort'; id: string };
            theme: { __typename?: 'FdrTheme'; id: string };
            tagline?: { __typename?: 'FdrTagline'; text: string } | null;
            image?: {
              __typename?: 'FdrImage';
              alt: string;
              description?: string | null;
              copyright?: string | null;
              rawUrl: string;
              resized: Array<{
                __typename?: 'FdrResizedImage';
                id: string;
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              }>;
            } | null;
            webMeta: {
              __typename?: 'FdrMeta';
              link: {
                __typename?: 'FdrLink';
                openMethod?: Types.FdrLinkOpenMethod | null;
                legacyUrl?: string | null;
                type: Types.FdrLinkType;
                url: string;
              };
            };
          }> | null;
          pagination: {
            __typename?: 'FdrPagination';
            total: number;
            page: number;
            perPage: number;
          };
        };
        requestButton: {
          __typename?: 'FdrRequestButton';
          staticCaption: string;
          objectCaption?: string | null;
          link: {
            __typename?: 'FdrLink';
            openMethod?: Types.FdrLinkOpenMethod | null;
            legacyUrl?: string | null;
            type: Types.FdrLinkType;
            url: string;
          };
        };
        boosters?: Array<{
          __typename?: 'FdrBooster';
          id: string;
          mainText: string;
          frequency?: Types.BoosterFrequency | null;
          duration: number;
          delay?: number | null;
        }> | null;
      }
    | {
        __typename: 'FdrCountryGroup';
        id: string;
        tagLine?: { __typename?: 'FdrTagline'; text: string } | null;
        children: {
          __typename?: 'FdrCountryGroupCountries';
          page?: Array<{
            __typename?: 'FdrCountry';
            name: string;
            id: string;
            hero?: {
              __typename?: 'FdrImage';
              alt: string;
              description?: string | null;
              copyright?: string | null;
              rawUrl: string;
              resized: Array<{
                __typename?: 'FdrResizedImage';
                id: string;
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              }>;
            } | null;
            webMeta: {
              __typename?: 'FdrGeoWebMeta';
              descriptionWebMeta?: {
                __typename?: 'FdrMeta';
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              } | null;
            };
          }> | null;
          pagination: {
            __typename?: 'FdrPagination';
            total: number;
            page: number;
            perPage: number;
          };
        };
        products?: {
          __typename?: 'FdrGeoProducts';
          productsPage?: Array<
            | {
                __typename: 'FdrAdventureTravel';
                teaser: string;
                name: string;
                id: string;
                bookmarked: boolean;
                headerImages?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                images?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                previews?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                route?: Array<{
                  __typename?: 'FdrAdventureTravelRoutePoint';
                  locationName: string;
                  coordinates: {
                    __typename?: 'FdrCoordinates';
                    latitude: number;
                    longitude: number;
                  };
                }> | null;
                webMeta: {
                  __typename?: 'FdrProductWebMeta';
                  meta?: {
                    __typename?: 'FdrMeta';
                    title: string;
                    description: string;
                    index: boolean;
                    noFollow: boolean;
                    sitemap: boolean;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  } | null;
                };
                featureGroups?: Array<{
                  __typename?: 'FdrProductFeatureGroup';
                  id: string;
                  name: string;
                  features: Array<{
                    __typename?: 'FdrProductFeature';
                    id: string;
                    name: string;
                    enabled: boolean;
                  }>;
                }> | null;
                productInfoB2B?: {
                  __typename?: 'ProductInfoB2B';
                  route?: string | null;
                  duration: string;
                  priceFrom?: string | null;
                  typeFlags?: Array<string> | null;
                } | null;
                googleStaticMapImage?: {
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                } | null;
              }
            | {
                __typename: 'FdrCruise';
                name: string;
                id: string;
                bookmarked: boolean;
              }
            | {
                __typename: 'FdrHotel';
                name: string;
                id: string;
                bookmarked: boolean;
                images?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                previews?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                webMeta: {
                  __typename?: 'FdrProductWebMeta';
                  meta?: {
                    __typename?: 'FdrMeta';
                    title: string;
                    description: string;
                    index: boolean;
                    noFollow: boolean;
                    sitemap: boolean;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  } | null;
                };
                flags?: {
                  __typename?: 'FdrHotelFlags';
                  extraOrdinary: boolean;
                } | null;
              }
            | {
                __typename: 'FdrRoundTrip';
                name: string;
                id: string;
                bookmarked: boolean;
              }
          > | null;
          pagination: {
            __typename?: 'FdrPagination';
            total: number;
            page: number;
            perPage: number;
          };
        };
        hotels: {
          __typename?: 'FdrGeoProducts';
          productsPage?: Array<
            | {
                __typename: 'FdrAdventureTravel';
                teaser: string;
                name: string;
                id: string;
                bookmarked: boolean;
                headerImages?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                images?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                previews?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                route?: Array<{
                  __typename?: 'FdrAdventureTravelRoutePoint';
                  locationName: string;
                  coordinates: {
                    __typename?: 'FdrCoordinates';
                    latitude: number;
                    longitude: number;
                  };
                }> | null;
                webMeta: {
                  __typename?: 'FdrProductWebMeta';
                  meta?: {
                    __typename?: 'FdrMeta';
                    title: string;
                    description: string;
                    index: boolean;
                    noFollow: boolean;
                    sitemap: boolean;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  } | null;
                };
                featureGroups?: Array<{
                  __typename?: 'FdrProductFeatureGroup';
                  id: string;
                  name: string;
                  features: Array<{
                    __typename?: 'FdrProductFeature';
                    id: string;
                    name: string;
                    enabled: boolean;
                  }>;
                }> | null;
                productInfoB2B?: {
                  __typename?: 'ProductInfoB2B';
                  route?: string | null;
                  duration: string;
                  priceFrom?: string | null;
                  typeFlags?: Array<string> | null;
                } | null;
              }
            | {
                __typename: 'FdrCruise';
                name: string;
                id: string;
                bookmarked: boolean;
              }
            | {
                __typename: 'FdrHotel';
                name: string;
                id: string;
                bookmarked: boolean;
                images?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                previews?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                webMeta: {
                  __typename?: 'FdrProductWebMeta';
                  meta?: {
                    __typename?: 'FdrMeta';
                    title: string;
                    description: string;
                    index: boolean;
                    noFollow: boolean;
                    sitemap: boolean;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  } | null;
                };
                flags?: {
                  __typename?: 'FdrHotelFlags';
                  extraOrdinary: boolean;
                } | null;
              }
            | {
                __typename: 'FdrRoundTrip';
                name: string;
                id: string;
                bookmarked: boolean;
              }
          > | null;
          pagination: {
            __typename?: 'FdrPagination';
            total: number;
            page: number;
            perPage: number;
          };
        };
        relatedBlogArticles: {
          __typename?: 'FdrAllBlogArticlesResponse';
          blogArticles?: Array<{
            __typename?: 'FdrBlogArticle';
            publicId: string;
            title?: string | null;
            text?: string | null;
            start?: any | null;
            flagline?: string | null;
            webMeta: {
              __typename?: 'FdrMeta';
              title: string;
              description: string;
              index: boolean;
              noFollow: boolean;
              sitemap: boolean;
              link: {
                __typename?: 'FdrLink';
                openMethod?: Types.FdrLinkOpenMethod | null;
                legacyUrl?: string | null;
                type: Types.FdrLinkType;
                url: string;
              };
            };
            hero?: {
              __typename?: 'FdrImage';
              resized: Array<{
                __typename?: 'FdrResizedImage';
                id: string;
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              }>;
            } | null;
            employee?: {
              __typename?: 'FdrEmployee';
              name: string;
              image?: {
                __typename?: 'FdrImage';
                resized: Array<{
                  __typename?: 'FdrResizedImage';
                  id: string;
                  link: {
                    __typename?: 'FdrLink';
                    openMethod?: Types.FdrLinkOpenMethod | null;
                    legacyUrl?: string | null;
                    type: Types.FdrLinkType;
                    url: string;
                  };
                }>;
              } | null;
            } | null;
          }> | null;
          pagination: {
            __typename?: 'FdrPagination';
            total: number;
            page: number;
            perPage: number;
          };
        };
        themeGeoPages: {
          __typename?: 'FdrThemeGeoPagesResponse';
          themeGeoPages?: Array<{
            __typename?: 'FdrThemeGeoPage';
            title: string;
            geo:
              | { __typename?: 'FdrContinent'; id: string }
              | { __typename?: 'FdrCountry'; id: string }
              | { __typename?: 'FdrCountryGroup'; id: string }
              | { __typename?: 'FdrDestination'; id: string }
              | { __typename?: 'FdrRegion'; id: string }
              | { __typename?: 'FdrResort'; id: string };
            theme: { __typename?: 'FdrTheme'; id: string };
            tagline?: { __typename?: 'FdrTagline'; text: string } | null;
            image?: {
              __typename?: 'FdrImage';
              alt: string;
              description?: string | null;
              copyright?: string | null;
              rawUrl: string;
              resized: Array<{
                __typename?: 'FdrResizedImage';
                id: string;
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              }>;
            } | null;
            webMeta: {
              __typename?: 'FdrMeta';
              link: {
                __typename?: 'FdrLink';
                openMethod?: Types.FdrLinkOpenMethod | null;
                legacyUrl?: string | null;
                type: Types.FdrLinkType;
                url: string;
              };
            };
          }> | null;
          pagination: {
            __typename?: 'FdrPagination';
            total: number;
            page: number;
            perPage: number;
          };
        };
        requestButton: {
          __typename?: 'FdrRequestButton';
          staticCaption: string;
          objectCaption?: string | null;
          link: {
            __typename?: 'FdrLink';
            openMethod?: Types.FdrLinkOpenMethod | null;
            legacyUrl?: string | null;
            type: Types.FdrLinkType;
            url: string;
          };
        };
        boosters?: Array<{
          __typename?: 'FdrBooster';
          id: string;
          mainText: string;
          frequency?: Types.BoosterFrequency | null;
          duration: number;
          delay?: number | null;
        }> | null;
      }
    | { __typename: 'FdrDestination'; id: string }
    | {
        __typename: 'FdrRegion';
        id: string;
        tagLine?: { __typename?: 'FdrTagline'; text: string } | null;
        products?: {
          __typename?: 'FdrGeoProducts';
          productsPage?: Array<
            | {
                __typename: 'FdrAdventureTravel';
                teaser: string;
                name: string;
                id: string;
                bookmarked: boolean;
                headerImages?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                images?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                previews?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                route?: Array<{
                  __typename?: 'FdrAdventureTravelRoutePoint';
                  locationName: string;
                  coordinates: {
                    __typename?: 'FdrCoordinates';
                    latitude: number;
                    longitude: number;
                  };
                }> | null;
                webMeta: {
                  __typename?: 'FdrProductWebMeta';
                  meta?: {
                    __typename?: 'FdrMeta';
                    title: string;
                    description: string;
                    index: boolean;
                    noFollow: boolean;
                    sitemap: boolean;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  } | null;
                };
                featureGroups?: Array<{
                  __typename?: 'FdrProductFeatureGroup';
                  id: string;
                  name: string;
                  features: Array<{
                    __typename?: 'FdrProductFeature';
                    id: string;
                    name: string;
                    enabled: boolean;
                  }>;
                }> | null;
                productInfoB2B?: {
                  __typename?: 'ProductInfoB2B';
                  route?: string | null;
                  duration: string;
                  priceFrom?: string | null;
                  typeFlags?: Array<string> | null;
                } | null;
                googleStaticMapImage?: {
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                } | null;
              }
            | {
                __typename: 'FdrCruise';
                name: string;
                id: string;
                bookmarked: boolean;
              }
            | {
                __typename: 'FdrHotel';
                name: string;
                id: string;
                bookmarked: boolean;
                images?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                previews?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                webMeta: {
                  __typename?: 'FdrProductWebMeta';
                  meta?: {
                    __typename?: 'FdrMeta';
                    title: string;
                    description: string;
                    index: boolean;
                    noFollow: boolean;
                    sitemap: boolean;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  } | null;
                };
                flags?: {
                  __typename?: 'FdrHotelFlags';
                  extraOrdinary: boolean;
                } | null;
              }
            | {
                __typename: 'FdrRoundTrip';
                name: string;
                id: string;
                bookmarked: boolean;
              }
          > | null;
          pagination: {
            __typename?: 'FdrPagination';
            total: number;
            page: number;
            perPage: number;
          };
        };
        hotels: {
          __typename?: 'FdrGeoProducts';
          productsPage?: Array<
            | {
                __typename: 'FdrAdventureTravel';
                teaser: string;
                name: string;
                id: string;
                bookmarked: boolean;
                headerImages?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                images?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                previews?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                route?: Array<{
                  __typename?: 'FdrAdventureTravelRoutePoint';
                  locationName: string;
                  coordinates: {
                    __typename?: 'FdrCoordinates';
                    latitude: number;
                    longitude: number;
                  };
                }> | null;
                webMeta: {
                  __typename?: 'FdrProductWebMeta';
                  meta?: {
                    __typename?: 'FdrMeta';
                    title: string;
                    description: string;
                    index: boolean;
                    noFollow: boolean;
                    sitemap: boolean;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  } | null;
                };
                featureGroups?: Array<{
                  __typename?: 'FdrProductFeatureGroup';
                  id: string;
                  name: string;
                  features: Array<{
                    __typename?: 'FdrProductFeature';
                    id: string;
                    name: string;
                    enabled: boolean;
                  }>;
                }> | null;
                productInfoB2B?: {
                  __typename?: 'ProductInfoB2B';
                  route?: string | null;
                  duration: string;
                  priceFrom?: string | null;
                  typeFlags?: Array<string> | null;
                } | null;
              }
            | {
                __typename: 'FdrCruise';
                name: string;
                id: string;
                bookmarked: boolean;
              }
            | {
                __typename: 'FdrHotel';
                name: string;
                id: string;
                bookmarked: boolean;
                images?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                previews?: Array<{
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }> | null;
                webMeta: {
                  __typename?: 'FdrProductWebMeta';
                  meta?: {
                    __typename?: 'FdrMeta';
                    title: string;
                    description: string;
                    index: boolean;
                    noFollow: boolean;
                    sitemap: boolean;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  } | null;
                };
                flags?: {
                  __typename?: 'FdrHotelFlags';
                  extraOrdinary: boolean;
                } | null;
              }
            | {
                __typename: 'FdrRoundTrip';
                name: string;
                id: string;
                bookmarked: boolean;
              }
          > | null;
          pagination: {
            __typename?: 'FdrPagination';
            total: number;
            page: number;
            perPage: number;
          };
        };
        relatedBlogArticles: {
          __typename?: 'FdrAllBlogArticlesResponse';
          blogArticles?: Array<{
            __typename?: 'FdrBlogArticle';
            publicId: string;
            title?: string | null;
            text?: string | null;
            start?: any | null;
            flagline?: string | null;
            webMeta: {
              __typename?: 'FdrMeta';
              title: string;
              description: string;
              index: boolean;
              noFollow: boolean;
              sitemap: boolean;
              link: {
                __typename?: 'FdrLink';
                openMethod?: Types.FdrLinkOpenMethod | null;
                legacyUrl?: string | null;
                type: Types.FdrLinkType;
                url: string;
              };
            };
            hero?: {
              __typename?: 'FdrImage';
              resized: Array<{
                __typename?: 'FdrResizedImage';
                id: string;
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              }>;
            } | null;
            employee?: {
              __typename?: 'FdrEmployee';
              name: string;
              image?: {
                __typename?: 'FdrImage';
                resized: Array<{
                  __typename?: 'FdrResizedImage';
                  id: string;
                  link: {
                    __typename?: 'FdrLink';
                    openMethod?: Types.FdrLinkOpenMethod | null;
                    legacyUrl?: string | null;
                    type: Types.FdrLinkType;
                    url: string;
                  };
                }>;
              } | null;
            } | null;
          }> | null;
          pagination: {
            __typename?: 'FdrPagination';
            total: number;
            page: number;
            perPage: number;
          };
        };
        themeGeoPages: {
          __typename?: 'FdrThemeGeoPagesResponse';
          themeGeoPages?: Array<{
            __typename?: 'FdrThemeGeoPage';
            title: string;
            geo:
              | { __typename?: 'FdrContinent'; id: string }
              | { __typename?: 'FdrCountry'; id: string }
              | { __typename?: 'FdrCountryGroup'; id: string }
              | { __typename?: 'FdrDestination'; id: string }
              | { __typename?: 'FdrRegion'; id: string }
              | { __typename?: 'FdrResort'; id: string };
            theme: { __typename?: 'FdrTheme'; id: string };
            tagline?: { __typename?: 'FdrTagline'; text: string } | null;
            image?: {
              __typename?: 'FdrImage';
              alt: string;
              description?: string | null;
              copyright?: string | null;
              rawUrl: string;
              resized: Array<{
                __typename?: 'FdrResizedImage';
                id: string;
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              }>;
            } | null;
            webMeta: {
              __typename?: 'FdrMeta';
              link: {
                __typename?: 'FdrLink';
                openMethod?: Types.FdrLinkOpenMethod | null;
                legacyUrl?: string | null;
                type: Types.FdrLinkType;
                url: string;
              };
            };
          }> | null;
          pagination: {
            __typename?: 'FdrPagination';
            total: number;
            page: number;
            perPage: number;
          };
        };
        requestButton: {
          __typename?: 'FdrRequestButton';
          staticCaption: string;
          objectCaption?: string | null;
          link: {
            __typename?: 'FdrLink';
            openMethod?: Types.FdrLinkOpenMethod | null;
            legacyUrl?: string | null;
            type: Types.FdrLinkType;
            url: string;
          };
        };
        boosters?: Array<{
          __typename?: 'FdrBooster';
          id: string;
          mainText: string;
          frequency?: Types.BoosterFrequency | null;
          duration: number;
          delay?: number | null;
        }> | null;
      }
    | { __typename: 'FdrResort'; id: string }
    | null;
};

export type GeoModuleHeroImageFragment = {
  __typename?: 'FdrImage';
  alt: string;
  description?: string | null;
  copyright?: string | null;
  rawUrl: string;
  resized: Array<{
    __typename?: 'FdrResizedImage';
    id: string;
    link: {
      __typename?: 'FdrLink';
      openMethod?: Types.FdrLinkOpenMethod | null;
      legacyUrl?: string | null;
      type: Types.FdrLinkType;
      url: string;
    };
  }>;
};

export const GeoModuleHeroImageFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'geoModuleHeroImage' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FdrImage' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'fdrImage' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'resized' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'criteria' },
                value: {
                  kind: 'ListValue',
                  values: [
                    {
                      kind: 'ObjectValue',
                      fields: [
                        {
                          kind: 'ObjectField',
                          name: { kind: 'Name', value: 'id' },
                          value: {
                            kind: 'StringValue',
                            value: '1x_mobile',
                            block: false,
                          },
                        },
                        {
                          kind: 'ObjectField',
                          name: { kind: 'Name', value: 'width' },
                          value: { kind: 'IntValue', value: '364' },
                        },
                        {
                          kind: 'ObjectField',
                          name: { kind: 'Name', value: 'height' },
                          value: { kind: 'IntValue', value: '315' },
                        },
                        {
                          kind: 'ObjectField',
                          name: { kind: 'Name', value: 'mode' },
                          value: { kind: 'EnumValue', value: 'AUTO' },
                        },
                      ],
                    },
                    {
                      kind: 'ObjectValue',
                      fields: [
                        {
                          kind: 'ObjectField',
                          name: { kind: 'Name', value: 'id' },
                          value: {
                            kind: 'StringValue',
                            value: '2x_mobile',
                            block: false,
                          },
                        },
                        {
                          kind: 'ObjectField',
                          name: { kind: 'Name', value: 'width' },
                          value: { kind: 'IntValue', value: '728' },
                        },
                        {
                          kind: 'ObjectField',
                          name: { kind: 'Name', value: 'height' },
                          value: { kind: 'IntValue', value: '630' },
                        },
                        {
                          kind: 'ObjectField',
                          name: { kind: 'Name', value: 'mode' },
                          value: { kind: 'EnumValue', value: 'AUTO' },
                        },
                      ],
                    },
                    {
                      kind: 'ObjectValue',
                      fields: [
                        {
                          kind: 'ObjectField',
                          name: { kind: 'Name', value: 'id' },
                          value: {
                            kind: 'StringValue',
                            value: '1x_desktop',
                            block: false,
                          },
                        },
                        {
                          kind: 'ObjectField',
                          name: { kind: 'Name', value: 'width' },
                          value: { kind: 'IntValue', value: '468' },
                        },
                        {
                          kind: 'ObjectField',
                          name: { kind: 'Name', value: 'height' },
                          value: { kind: 'IntValue', value: '388' },
                        },
                        {
                          kind: 'ObjectField',
                          name: { kind: 'Name', value: 'mode' },
                          value: { kind: 'EnumValue', value: 'AUTO' },
                        },
                      ],
                    },
                    {
                      kind: 'ObjectValue',
                      fields: [
                        {
                          kind: 'ObjectField',
                          name: { kind: 'Name', value: 'id' },
                          value: {
                            kind: 'StringValue',
                            value: '2x_desktop',
                            block: false,
                          },
                        },
                        {
                          kind: 'ObjectField',
                          name: { kind: 'Name', value: 'width' },
                          value: { kind: 'IntValue', value: '936' },
                        },
                        {
                          kind: 'ObjectField',
                          name: { kind: 'Name', value: 'height' },
                          value: { kind: 'IntValue', value: '776' },
                        },
                        {
                          kind: 'ObjectField',
                          name: { kind: 'Name', value: 'mode' },
                          value: { kind: 'EnumValue', value: 'AUTO' },
                        },
                      ],
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'fdrResized' },
                },
              ],
            },
          },
        ],
      },
    },
    ...FdrImageFragmentDoc.definitions,
    ...FdrResizedFragmentDoc.definitions,
  ],
} as unknown as DocumentNode;
export const FdrGeoModulesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'FdrGeoModules' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: {
            kind: 'Variable',
            name: { kind: 'Name', value: 'input' },
          },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FdrGeoInput' },
            },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: {
            kind: 'Variable',
            name: { kind: 'Name', value: 'childrenInput' },
          },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FdrGeoChildrenInput' },
            },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: {
            kind: 'Variable',
            name: { kind: 'Name', value: 'productsInput' },
          },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FdrGeoProductsInput' },
            },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: {
            kind: 'Variable',
            name: { kind: 'Name', value: 'hotelsInput' },
          },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FdrGeoProductsInput' },
            },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: {
            kind: 'Variable',
            name: { kind: 'Name', value: 'blogCriteria' },
          },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'ListType',
              type: {
                kind: 'NonNullType',
                type: {
                  kind: 'NamedType',
                  name: { kind: 'Name', value: 'FdrImageCriteria' },
                },
              },
            },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: {
            kind: 'Variable',
            name: { kind: 'Name', value: 'blogArticleInput' },
          },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FdrAllBlogArticlesInput' },
            },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: {
            kind: 'Variable',
            name: { kind: 'Name', value: 'themeGeoInput' },
          },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FdrThemeGeoPagesInput' },
            },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: {
            kind: 'Variable',
            name: { kind: 'Name', value: 'isB2B' },
          },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fdrGeo' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: {
                  kind: 'Variable',
                  name: { kind: 'Name', value: 'input' },
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'id' },
                  name: { kind: 'Name', value: 'publicId' },
                },
                { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
                {
                  kind: 'InlineFragment',
                  typeCondition: {
                    kind: 'NamedType',
                    name: { kind: 'Name', value: 'FdrContinent' },
                  },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'tagLine' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'text' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        alias: { kind: 'Name', value: 'children' },
                        name: { kind: 'Name', value: 'countries' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'childrenInput' },
                            },
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              alias: { kind: 'Name', value: 'page' },
                              name: { kind: 'Name', value: 'countriesPage' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    alias: { kind: 'Name', value: 'id' },
                                    name: { kind: 'Name', value: 'publicId' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'name' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'hero' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'FragmentSpread',
                                          name: {
                                            kind: 'Name',
                                            value: 'geoModuleHeroImage',
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'webMeta' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'FragmentSpread',
                                          name: {
                                            kind: 'Name',
                                            value: 'descriptionGeoLink',
                                          },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'pagination' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: {
                                      kind: 'Name',
                                      value: 'fdrPagination',
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'products' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'productsInput' },
                            },
                          },
                        ],
                        directives: [
                          {
                            kind: 'Directive',
                            name: { kind: 'Name', value: 'skip' },
                            arguments: [
                              {
                                kind: 'Argument',
                                name: { kind: 'Name', value: 'if' },
                                value: {
                                  kind: 'Variable',
                                  name: { kind: 'Name', value: 'isB2B' },
                                },
                              },
                            ],
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrGeoProduct' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'products' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'productsInput' },
                            },
                          },
                        ],
                        directives: [
                          {
                            kind: 'Directive',
                            name: { kind: 'Name', value: 'include' },
                            arguments: [
                              {
                                kind: 'Argument',
                                name: { kind: 'Name', value: 'if' },
                                value: {
                                  kind: 'Variable',
                                  name: { kind: 'Name', value: 'isB2B' },
                                },
                              },
                            ],
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrGeoProductB2b' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        alias: { kind: 'Name', value: 'hotels' },
                        name: { kind: 'Name', value: 'products' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'hotelsInput' },
                            },
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrGeoProduct' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'relatedBlogArticles' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'blogArticleInput' },
                            },
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: {
                                kind: 'Name',
                                value: 'fdrAllBlogArticlesResponse',
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'themeGeoPages' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'themeGeoInput' },
                            },
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrThemeGeoPages' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'requestButton' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrRequestButton' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'boosters' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrBooster' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: {
                    kind: 'NamedType',
                    name: { kind: 'Name', value: 'FdrCountryGroup' },
                  },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'tagLine' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'text' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        alias: { kind: 'Name', value: 'children' },
                        name: { kind: 'Name', value: 'countries' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'childrenInput' },
                            },
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              alias: { kind: 'Name', value: 'page' },
                              name: { kind: 'Name', value: 'countriesPage' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    alias: { kind: 'Name', value: 'id' },
                                    name: { kind: 'Name', value: 'publicId' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'name' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'hero' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'FragmentSpread',
                                          name: {
                                            kind: 'Name',
                                            value: 'geoModuleHeroImage',
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'webMeta' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'FragmentSpread',
                                          name: {
                                            kind: 'Name',
                                            value: 'descriptionGeoLink',
                                          },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'pagination' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: {
                                      kind: 'Name',
                                      value: 'fdrPagination',
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'products' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'productsInput' },
                            },
                          },
                        ],
                        directives: [
                          {
                            kind: 'Directive',
                            name: { kind: 'Name', value: 'skip' },
                            arguments: [
                              {
                                kind: 'Argument',
                                name: { kind: 'Name', value: 'if' },
                                value: {
                                  kind: 'Variable',
                                  name: { kind: 'Name', value: 'isB2B' },
                                },
                              },
                            ],
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrGeoProduct' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'products' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'productsInput' },
                            },
                          },
                        ],
                        directives: [
                          {
                            kind: 'Directive',
                            name: { kind: 'Name', value: 'include' },
                            arguments: [
                              {
                                kind: 'Argument',
                                name: { kind: 'Name', value: 'if' },
                                value: {
                                  kind: 'Variable',
                                  name: { kind: 'Name', value: 'isB2B' },
                                },
                              },
                            ],
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrGeoProductB2b' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        alias: { kind: 'Name', value: 'hotels' },
                        name: { kind: 'Name', value: 'products' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'hotelsInput' },
                            },
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrGeoProduct' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'relatedBlogArticles' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'blogArticleInput' },
                            },
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: {
                                kind: 'Name',
                                value: 'fdrAllBlogArticlesResponse',
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'themeGeoPages' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'themeGeoInput' },
                            },
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrThemeGeoPages' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'requestButton' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrRequestButton' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'boosters' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrBooster' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: {
                    kind: 'NamedType',
                    name: { kind: 'Name', value: 'FdrCountry' },
                  },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'tagLine' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'text' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        alias: { kind: 'Name', value: 'children' },
                        name: { kind: 'Name', value: 'regions' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'childrenInput' },
                            },
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              alias: { kind: 'Name', value: 'page' },
                              name: { kind: 'Name', value: 'regionsPage' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    alias: { kind: 'Name', value: 'id' },
                                    name: { kind: 'Name', value: 'publicId' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'name' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'hero' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'FragmentSpread',
                                          name: {
                                            kind: 'Name',
                                            value: 'geoModuleHeroImage',
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'webMeta' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'FragmentSpread',
                                          name: {
                                            kind: 'Name',
                                            value: 'descriptionGeoLink',
                                          },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'pagination' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: {
                                      kind: 'Name',
                                      value: 'fdrPagination',
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'products' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'productsInput' },
                            },
                          },
                        ],
                        directives: [
                          {
                            kind: 'Directive',
                            name: { kind: 'Name', value: 'skip' },
                            arguments: [
                              {
                                kind: 'Argument',
                                name: { kind: 'Name', value: 'if' },
                                value: {
                                  kind: 'Variable',
                                  name: { kind: 'Name', value: 'isB2B' },
                                },
                              },
                            ],
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrGeoProduct' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'products' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'productsInput' },
                            },
                          },
                        ],
                        directives: [
                          {
                            kind: 'Directive',
                            name: { kind: 'Name', value: 'include' },
                            arguments: [
                              {
                                kind: 'Argument',
                                name: { kind: 'Name', value: 'if' },
                                value: {
                                  kind: 'Variable',
                                  name: { kind: 'Name', value: 'isB2B' },
                                },
                              },
                            ],
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrGeoProductB2b' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        alias: { kind: 'Name', value: 'hotels' },
                        name: { kind: 'Name', value: 'products' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'hotelsInput' },
                            },
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrGeoProduct' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'relatedBlogArticles' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'blogArticleInput' },
                            },
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: {
                                kind: 'Name',
                                value: 'fdrAllBlogArticlesResponse',
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'themeGeoPages' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'themeGeoInput' },
                            },
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrThemeGeoPages' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'requestButton' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrRequestButton' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'boosters' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrBooster' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: {
                    kind: 'NamedType',
                    name: { kind: 'Name', value: 'FdrRegion' },
                  },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'tagLine' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'text' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'products' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'productsInput' },
                            },
                          },
                        ],
                        directives: [
                          {
                            kind: 'Directive',
                            name: { kind: 'Name', value: 'skip' },
                            arguments: [
                              {
                                kind: 'Argument',
                                name: { kind: 'Name', value: 'if' },
                                value: {
                                  kind: 'Variable',
                                  name: { kind: 'Name', value: 'isB2B' },
                                },
                              },
                            ],
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrGeoProduct' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'products' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'productsInput' },
                            },
                          },
                        ],
                        directives: [
                          {
                            kind: 'Directive',
                            name: { kind: 'Name', value: 'include' },
                            arguments: [
                              {
                                kind: 'Argument',
                                name: { kind: 'Name', value: 'if' },
                                value: {
                                  kind: 'Variable',
                                  name: { kind: 'Name', value: 'isB2B' },
                                },
                              },
                            ],
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrGeoProductB2b' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        alias: { kind: 'Name', value: 'hotels' },
                        name: { kind: 'Name', value: 'products' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'hotelsInput' },
                            },
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrGeoProduct' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'relatedBlogArticles' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'blogArticleInput' },
                            },
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: {
                                kind: 'Name',
                                value: 'fdrAllBlogArticlesResponse',
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'themeGeoPages' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'input' },
                            value: {
                              kind: 'Variable',
                              name: { kind: 'Name', value: 'themeGeoInput' },
                            },
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrThemeGeoPages' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'requestButton' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrRequestButton' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'boosters' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'fdrBooster' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    ...GeoModuleHeroImageFragmentDoc.definitions,
    ...DescriptionGeoLinkFragmentDoc.definitions,
    ...FdrPaginationFragmentDoc.definitions,
    ...FdrGeoProductFragmentDoc.definitions,
    ...FdrGeoProductB2bFragmentDoc.definitions,
    ...FdrAllBlogArticlesResponseFragmentDoc.definitions,
    ...FdrThemeGeoPagesFragmentDoc.definitions,
    ...FdrRequestButtonFragmentDoc.definitions,
    ...FdrBoosterFragmentDoc.definitions,
  ],
} as unknown as DocumentNode;

/**
 * __useFdrGeoModulesQuery__
 *
 * To run a query within a React component, call `useFdrGeoModulesQuery` and pass it any options that fit your needs.
 * When your component renders, `useFdrGeoModulesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useFdrGeoModulesQuery({
 *   variables: {
 *      input: // value for 'input'
 *      childrenInput: // value for 'childrenInput'
 *      productsInput: // value for 'productsInput'
 *      hotelsInput: // value for 'hotelsInput'
 *      blogCriteria: // value for 'blogCriteria'
 *      blogArticleInput: // value for 'blogArticleInput'
 *      themeGeoInput: // value for 'themeGeoInput'
 *      isB2B: // value for 'isB2B'
 *   },
 * });
 */
export function useFdrGeoModulesQuery(
  baseOptions: ApolloReactHooks.QueryHookOptions<
    FdrGeoModulesQuery,
    FdrGeoModulesQueryVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return ApolloReactHooks.useQuery<
    FdrGeoModulesQuery,
    FdrGeoModulesQueryVariables
  >(FdrGeoModulesDocument, options);
}
export function useFdrGeoModulesLazyQuery(
  baseOptions?: ApolloReactHooks.LazyQueryHookOptions<
    FdrGeoModulesQuery,
    FdrGeoModulesQueryVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return ApolloReactHooks.useLazyQuery<
    FdrGeoModulesQuery,
    FdrGeoModulesQueryVariables
  >(FdrGeoModulesDocument, options);
}
export type FdrGeoModulesQueryHookResult = ReturnType<
  typeof useFdrGeoModulesQuery
>;
export type FdrGeoModulesLazyQueryHookResult = ReturnType<
  typeof useFdrGeoModulesLazyQuery
>;
export type FdrGeoModulesQueryResult = Apollo.QueryResult<
  FdrGeoModulesQuery,
  FdrGeoModulesQueryVariables
>;
